import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import * as Icons from "lucide-react";
import { Flame, CalendarDays, BookOpen, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { PROGRESS } from "@/constants/testIds";

const BOOST_META = {
  daily_checkin: { icon: "Activity", to: "/wellness" },
  lesson: { icon: "BookOpen", to: "/learn" },
  journal: { icon: "NotebookPen", to: "/journal" },
  coach: { icon: "MessageCircle", to: "/coach" },
  streak: { icon: "Flame", to: "/wellness" },
  community: { icon: "Heart", to: "/community" },
};

function celebrate() {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const colors = ["#C89B55", "#E7C98F", "#291B2D", "#3D6B52"];
  const base = { spread: 55, startVelocity: 28, ticks: 120, gravity: 0.9, scalar: 0.9, colors, disableForReducedMotion: true, zIndex: 90 };
  confetti({ ...base, particleCount: 45, origin: { x: 0.35, y: 0.32 } });
  setTimeout(() => confetti({ ...base, particleCount: 35, origin: { x: 0.65, y: 0.32 } }), 140);
}

function AnimatedNumber({ from, to }) {
  const [n, setN] = useState(from ?? to);
  useEffect(() => {
    const start = from ?? to;
    if (start === to) { setN(to); return; }
    let raf; const t0 = performance.now(); const dur = 1000;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.round(start + (to - start) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to]);
  return <>{n}</>;
}

function StatCard({ icon: Icon, value, label }) {
  return (
    <div className="bg-white border border-[#291B2D]/5 rounded-2xl p-5 shadow-[0_4px_20px_rgba(41,27,45,0.04)]">
      <Icon className="h-5 w-5 text-[#C89B55] mb-3" strokeWidth={1.9} />
      <p className="font-display text-3xl font-semibold">{value}</p>
      <p className="text-sm text-[#8A838E] mt-0.5">{label}</p>
    </div>
  );
}

export default function Progress() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [prevScore, setPrevScore] = useState(null);

  useEffect(() => {
    track("progress_viewed");
    api.get("/progress").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  // Celebrate the bump: if the score rose since last visit, toast + animate from the old value.
  useEffect(() => {
    if (!data || data.wellness_score == null) return;
    const raw = localStorage.getItem("sh_ws_last");
    const last = raw == null ? null : Number(raw);
    if (last != null && !Number.isNaN(last) && data.wellness_score > last) {
      const delta = data.wellness_score - last;
      setPrevScore(last);
      toast.success(`You earned +${delta} point${delta > 1 ? "s" : ""} — your wellness score rose to ${data.wellness_score}!`);
      celebrate();
    } else if (prevScore == null) {
      setPrevScore(data.wellness_score);
    }
    localStorage.setItem("sh_ws_last", String(data.wellness_score));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data) return <div className="h-64 flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-[#C89B55] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid={PROGRESS.page}>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your journey</p>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1">How far you've come</h1>
      </div>

      {/* Wellness score + ways to lift it */}
      <div data-testid="progress-wellness" className="rounded-3xl bg-[#291B2D] text-[#FCFAF6] p-7 lg:p-9">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your Wellness Score</p>
            {data.wellness_score != null ? (
              <div className="flex items-end gap-2 mt-2">
                <span data-testid="progress-score-value" className="font-display text-6xl font-light leading-none"><AnimatedNumber from={prevScore} to={data.wellness_score} /></span>
                <span className="text-white/50 mb-2">/ 100</span>
              </div>
            ) : (
              <p className="mt-2 text-white/70 max-w-xs">Take your wellness check-in to see your score and start earning points.</p>
            )}
          </div>
          {data.potential_increase > 0 && (
            <div className="text-right">
              <div data-testid="progress-potential-increase" className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B55]/20 text-[#E7C98F] px-4 py-2 text-sm font-semibold">
                <Icons.TrendingUp className="h-4 w-4" /> +{data.potential_increase} points to gain
              </div>
              {data.potential_score != null && (
                <p className="text-white/50 text-xs mt-2">Reach up to <span className="text-white font-medium">{data.potential_score}/100</span> this week</p>
              )}
            </div>
          )}
        </div>

        {data.wellness_score != null && (
          <div className="mt-5 h-2 rounded-full bg-white/10 overflow-hidden">
            <motion.div className="h-full bg-[#C89B55]" initial={{ width: 0 }} animate={{ width: `${data.wellness_score}%` }} transition={{ duration: 1, ease: "easeOut" }} />
          </div>
        )}

        <p className="mt-6 text-sm text-white/70">Small, steady actions lift your score. Here's where to earn points this week:</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {data.score_boosts?.map((b) => {
            const meta = BOOST_META[b.id] || {};
            const Icon = Icons[meta.icon] || Icons.Sparkles;
            return (
              <button key={b.id} data-testid={`progress-boost-${b.id}`} onClick={() => navigate(meta.to || "/home")}
                className={`text-left rounded-2xl p-4 flex items-center gap-3 transition-all ${b.done ? "bg-white/5" : "bg-white/10 hover:bg-white/15 hover:-translate-y-0.5"}`}>
                <span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center ${b.done ? "bg-[#3D6B52]/30 text-[#8FCBA4]" : "bg-[#C89B55]/20 text-[#E7C98F]"}`}>
                  {b.done ? <Icons.Check className="h-5 w-5" /> : <Icon className="h-5 w-5" strokeWidth={1.8} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm">{b.label}</span>
                    <span className={`text-xs font-semibold shrink-0 ${b.done ? "text-[#8FCBA4]" : "text-[#E7C98F]"}`}>{b.done ? "Earned" : `+${b.points}`}</span>
                  </span>
                  <span className="block text-xs text-white/50 mt-0.5 truncate">{b.description}</span>
                </span>
              </button>
            );
          })}
        </div>
        {data.wellness_score == null && (
          <button data-testid="progress-take-checkin" onClick={() => navigate("/assessment")}
            className="mt-6 rounded-full bg-[#C89B55] text-[#291B2D] px-6 py-3 text-sm font-semibold hover:bg-[#B38743] transition-smooth">
            Take your wellness check-in
          </button>
        )}
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Flame} value={data.streak} label={data.streak === 1 ? "day streak" : "day streak"} />
        <StatCard icon={CalendarDays} value={data.days_active} label="active days" />
        <StatCard icon={BookOpen} value={data.lessons_completed} label="lessons completed" />
        <StatCard icon={Icons.NotebookPen} value={data.journal_entries} label="journal entries" />
      </div>

      {/* Milestones — Oura/Apple Fitness aesthetic */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-4">Milestones</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
          {data.milestones.map((m, i) => {
            const Icon = Icons[m.icon] || Icons.Award;
            return (
              <motion.div key={m.id} data-testid={`${PROGRESS.milestone}-${m.id}`}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: i * 0.05 }}
                className={`relative aspect-square rounded-2xl flex flex-col items-center justify-center p-4 text-center ${
                  m.earned
                    ? "bg-gradient-to-br from-[#C89B55]/25 to-[#C89B55]/5 border border-[#C89B55]/25 shadow-sm"
                    : "bg-white border border-[#291B2D]/5 opacity-60 grayscale"
                }`}>
                {!m.earned && <Lock className="absolute top-2 right-2 h-3.5 w-3.5 text-[#8A838E]" />}
                <Icon className={`h-9 w-9 mb-2 ${m.earned ? "text-[#B38743]" : "text-[#8A838E]"}`} strokeWidth={1.6} />
                <span className="text-xs font-medium text-[#252326]">{m.label}</span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* My Story timeline */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-5">My story</h2>
        {data.timeline.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-center bg-white rounded-3xl border border-[#291B2D]/5">
            <span className="w-14 h-14 rounded-full bg-[#F1EDE0] text-[#C89B55] flex items-center justify-center mb-3">
              <Icons.Sparkles className="h-6 w-6" />
            </span>
            <p className="font-display text-lg">Your story starts today.</p>
            <p className="text-sm text-[#8A838E] mt-1 max-w-sm">Every lesson, reflection, and check-in becomes a chapter here.</p>
          </div>
        ) : (
          <div className="relative pl-8 space-y-6 before:absolute before:inset-y-0 before:left-[15px] before:w-px before:bg-[#291B2D]/10">
            {data.timeline.map((e, i) => {
              const Icon = e.type === "lesson" ? BookOpen : e.type === "journal" ? Icons.NotebookPen : Icons.Activity;
              return (
                <div key={i} className="relative">
                  <span className="absolute -left-8 w-8 h-8 rounded-full bg-[#F1EDE0] border-4 border-[#FCFAF6] flex items-center justify-center text-[#C89B55] z-10">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="bg-white p-5 rounded-2xl shadow-[0_4px_20px_rgba(41,27,45,0.04)] border border-[#291B2D]/5">
                    <p className="font-medium text-[#252326]">{e.label}</p>
                    <p className="text-xs text-[#8A838E] mt-1">
                      {new Date(e.at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
